import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { PencilIcon } from '@heroicons/react/20/solid';
import { baseAPI } from '../../../config';
import settingsStore from '../../../store/settingsStore';
import AddMaytapiPhone from './AddMaytapiPhone';
import EditMaytapiPhone from './EditMaytapiPhone';
import { DEFAULT_PHONE_STATUS } from '@claimup/shared/constants/phones';
import {
  getPhoneStatusLabel,
  getPhoneStatusColors,
  getCapacityStatusColors,
  formatCapacity,
} from './phoneUtils';

/**
 * Qué hay que hacer con la SESIÓN de WhatsApp de esta línea. Casi siempre: nada.
 *
 * 🔴 No es lo mismo que la columna «Estado». Esa es un interruptor que pone una
 * persona; esto es la realidad. El 11/09/2026 dos líneas llevaban caídas —una
 * con la sesión cerrada en el móvil y otra que no cargaba WhatsApp Web— con
 * **236 tareas atadas a ellas**, las dos figurando «Activo», y Edgar se enteró
 * mirando el móvil.
 *
 * 🔴 Y esto ESTUVO MIDIENDO LO CONTRARIO DE LO QUE QUERÍA MEDIR. Tenía tres
 * bandas por tiempo —Viva / Fuera de turno / Sin noticias— calculadas sobre
 * `visto_at`, que NO es «la línea está viva»: es «la última vez que el brazo
 * habló de esta línea», y eso lo escribe al REPORTAR, al final de una pasada.
 * Una línea que se pasa tres horas enviando no reporta hasta que acaba, así que
 * salía en ámbar con «¿está corriendo el brazo?» — mientras era la única que
 * estaba trabajando. El 14/09/2026 avisó de `claimup.io` y `Samsung Pequeño
 * Eden`, que en ese momento llevaban 21 mensajes enviados en quince minutos, y
 * dio por buenas a las que llevaban paradas desde las 11:04.
 *
 * Así que el tiempo se fue. Lo único que se avisa es lo único accionable, y lo
 * dijo Edgar: **que la sesión se ha cerrado, para volver a pasar el QR**. Eso no
 * se deduce de un reloj — lo dice el brazo, que al abrir la sesión intenta leer
 * el número y no puede.
 *
 * Se separan los dos motivos porque la acción es distinta: si WhatsApp pide el
 * QR hay que escanear; si el navegador no abre, escanear no arregla nada.
 *
 * Solo aplica al navegador: una línea de Maytapi es una API, no tiene sesión que
 * abrir y su `visto_at` está vacío desde siempre — por eso nunca se le pide.
 */

/** Un motivo del brazo, traducido a lo que hay que hacer. */
function queHacer(motivo = '') {
  const m = String(motivo);
  // `sin_numero_legible` y «ya no está vinculada» son la misma avería vista en
  // dos momentos: al abrir, y a mitad de la pasada.
  if (/sin_numero_legible|no esta vinculada|no está vinculada/i.test(m)) {
    return { titulo: 'Sesión cerrada', pie: 'WhatsApp está pidiendo el QR: hay que volver a escanearlo' };
  }
  if (/^no_abre|no responde/i.test(m)) {
    return { titulo: 'El navegador no abre', pie: `Escanear el QR no lo arregla. ${m}` };
  }
  if (/tiene el número|tiene el numero/i.test(m)) {
    return { titulo: 'Vinculada a otro móvil', pie: m };
  }
  return { titulo: 'Sesión caída', pie: m || 'el brazo no pudo leer el número de la sesión' };
}

const EstadoDeSesion = ({ phone }) => {
  if (phone.canal !== 'navegador') return <span className='text-slate-300'>—</span>;

  if (!phone.visto_at) {
    return (
      <span className='text-xs text-slate-400' title='El brazo aún no ha reportado esta línea'>
        sin comprobar
      </span>
    );
  }

  const minutos = (Date.now() - new Date(phone.visto_at)) / 60000;
  const cuando =
    minutos < 60
      ? `hace ${Math.max(1, Math.round(minutos))} min`
      : `hace ${Math.round(minutos / 60)} h`;

  if (phone.visto_ok === false) {
    const { titulo, pie } = queHacer(phone.visto_motivo);
    return (
      <span className='inline-flex flex-col'>
        <span className='inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium bg-red-100 text-red-800 w-fit'>
          {titulo}
        </span>
        <span className='text-[11px] text-red-700 mt-0.5'>{pie}</span>
      </span>
    );
  }

  // Todo bien: se dice cuándo se comprobó y ya está. Sin color y sin etiqueta —
  // un badge en doce filas a la vez no informa de nada, solo enseña a ignorarlos.
  return <span className='text-[11px] text-slate-500'>comprobada {cuando}</span>;
};

const MaytapiPhones = () => {
  const { maytapiPhones, setMaytapiPhones } = settingsStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingPhone, setEditingPhone] = useState(null);

  const loadPhones = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${baseAPI}/settings/phones`, { headers });
      const list = Array.isArray(response.data?.data) ? response.data?.data : [];

      setMaytapiPhones(list);
    } catch (err) {
      setError('Error al cargar los teléfonos de Maytapi');
      // eslint-disable-next-line no-console
      console.error('Error loading Maytapi phones:', err);
    } finally {
      setIsLoading(false);
    }
  }, [setMaytapiPhones]);

  useEffect(() => {
    loadPhones();
  }, [loadPhones]);

  const handleEditClick = phone => {
    setEditingPhone(phone);
  };

  const handleCloseEdit = () => {
    setEditingPhone(null);
    loadPhones(); // Reload to refresh stats
  };

  return (
    <div className='bg-white rounded-lg border border-slate-200 overflow-hidden'>
      <div className='px-6 py-4 border-b border-slate-200 bg-slate-50'>
        <div className='flex items-center justify-between'>
          <h3 className='text-lg font-medium text-slate-900'>Teléfonos Maytapi</h3>
          <div className='flex items-center gap-2'>
            <AddMaytapiPhone />
          </div>
        </div>
      </div>
      <div className='p-6'>
        {isLoading ? (
          <div className='text-center py-8'>
            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto'></div>
            <p className='text-sm text-slate-500 mt-2'>Cargando teléfonos...</p>
          </div>
        ) : error ? (
          <div className='text-center py-8'>
            <p className='text-sm text-red-600'>{error}</p>
          </div>
        ) : maytapiPhones.length === 0 ? (
          <div className='text-center py-8'>
            <p className='text-sm text-slate-500'>No hay teléfonos activos.</p>
          </div>
        ) : (
          <div className='overflow-x-auto'>
            <table className='min-w-full divide-y divide-slate-200'>
              <thead className='bg-slate-50'>
                <tr>
                  <th
                    scope='col'
                    className='px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider'
                  >
                    Nombre
                  </th>
                  <th
                    scope='col'
                    className='px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider'
                  >
                    ID
                  </th>
                  <th
                    scope='col'
                    className='px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider'
                  >
                    Número de teléfono
                  </th>
                  <th
                    scope='col'
                    className='px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider'
                  >
                    Canal
                  </th>
                  <th
                    scope='col'
                    className='px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider'
                  >
                    Estado
                  </th>
                  <th
                    scope='col'
                    className='px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider'
                  >
                    Nuevas hoy
                  </th>
                  <th
                    scope='col'
                    className='px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider'
                  >
                    Activas
                  </th>
                  <th
                    scope='col'
                    className='px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider'
                  >
                    Sesión
                  </th>
                  <th
                    scope='col'
                    className='px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider'
                  >
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className='bg-white divide-y divide-slate-200'>
                {maytapiPhones.map(phone => (
                  <tr key={phone._id}>
                    <td className='px-4 py-3 text-sm text-slate-900'>{phone.name || '-'}</td>
                    <td className='px-4 py-3 text-sm text-slate-900'>{phone.phone_id}</td>
                    <td className='px-4 py-3 text-sm text-slate-700'>{phone.phone_number} </td>
                    {/* Por donde sale el mensaje. Una linea de navegador no tiene API:
                        sus envios los ejecuta un Chrome real y son asincronos. */}
                    <td className='px-4 py-3 text-sm'>
                      <span className='inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium bg-slate-100 text-slate-700'>
                        {phone.canal === 'navegador' ? 'Navegador' : 'Maytapi'}
                      </span>
                    </td>
                    <td className='px-4 py-3 text-sm'>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPhoneStatusColors(
                          phone.status || DEFAULT_PHONE_STATUS
                        )}`}
                      >
                        {getPhoneStatusLabel(phone.status || DEFAULT_PHONE_STATUS)}
                      </span>
                    </td>
                    <td className='px-4 py-3 text-sm'>
                      <span
                        className={getCapacityStatusColors(
                          phone.current_new_conversations || 0,
                          phone.max_new_conversations
                        )}
                      >
                        {formatCapacity(phone.current_new_conversations || 0, phone.max_new_conversations)}
                      </span>
                    </td>
                    {/* El latido: si la sesión de WhatsApp está viva de verdad.
                        Distinto de «Estado», que es el interruptor que pone una
                        persona — una línea puede estar activa y tener la sesión
                        cerrada, y eso es justo lo que nadie veía. */}
                    <td className='px-4 py-3 text-sm'>
                      <EstadoDeSesion phone={phone} />
                    </td>
                    <td className='px-4 py-3 text-sm'>
                      <span
                        className={getCapacityStatusColors(
                          phone.current_active_conversations || 0,
                          phone.max_active_conversations
                        )}
                      >
                        {formatCapacity(phone.current_active_conversations || 0, phone.max_active_conversations)}
                      </span>
                    </td>
                    <td className='px-4 py-3 text-sm'>
                      <div className='flex items-center gap-2'>
                        <button
                          type='button'
                          onClick={() => handleEditClick(phone)}
                          className='inline-flex items-center px-2 py-1 text-sm font-medium text-slate-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors'
                          title='Editar teléfono'
                        >
                          <PencilIcon className='h-4 w-4' />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editingPhone && <EditMaytapiPhone phone={editingPhone} isOpen={!!editingPhone} onClose={handleCloseEdit} />}

      {/* Stats Modal */}
    </div>
  );
};

export default MaytapiPhones;

// Se exporta para poder probarlo solo: es la celda que decide si Edgar tiene que
// levantarse a escanear un QR, y equivocarse ahi cuesta una linea parada un dia
// entero. El resto de la pantalla es una tabla.
export { EstadoDeSesion };
